import { ShieldCheck } from 'lucide-react';
import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useLocation } from 'react-router-dom';

import { PrivacyToggleProvider } from '@eluve/blocks';
import { Box, Heading, LinkButton, LoadingIndicator } from '@eluve/components';
import { useSession } from '@eluve/session-helpers';
import { TabNav } from '@eluve/smart-blocks';

import { NotFoundPage } from '../../NotFoundPage';

import { AdminBreadcrumbs } from './components/AdminBreadcrumbs';

const adminRoutes = [
  { name: 'Organizations', link: 'tenants' },
  { name: 'Prompts', link: 'prompts?isActive=true' },
  { name: 'Output Templates', link: 'output-templates?isActive=true' },
  { name: 'Models', link: 'models' },
  { name: 'Fact Verification', link: 'fact-verification' },
  { name: 'Experiments', link: 'experiments' },
  { name: 'Audit Trail', link: 'audit-trail' },
  { name: 'Global Feature Flags', link: 'global-feature-flags' },
  { name: 'Users', link: 'users' },
  { name: 'API Tokens', link: 'api-tokens' },
  { name: 'Reviewers', link: 'reviewers-list' },
  { name: 'Site Banners', link: 'site-banners' },
];

export const AdminLayout: React.FC = () => {
  const location = useLocation();
  const session = useSession();

  const isCloning = location.pathname.includes('clone');
  const isCreating = location.pathname.includes('create');
  const isOutputTemplates = location.pathname.includes('output-templates');
  const isPrompts = location.pathname.includes('prompts');
  const shouldHideNavigation =
    (isPrompts || isOutputTemplates) && (isCloning || isCreating);

  if (!session.isEluveAdmin) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Helmet>
        <title>Admin | Eluve</title>
      </Helmet>
      <div className="min-h-full overflow-x-hidden">
        <div className="bg-text-primary pb-32">
          {!shouldHideNavigation && (
            <header className="py-4 lg:py-8">
              <div className="mx-auto w-full px-2 lg:px-8">
                <div className="flex flex-wrap items-center justify-between gap-y-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-10 text-border-yellow-subtle" />
                    <Heading
                      size="3xl"
                      weight="bold"
                      className="tracking-tight text-white"
                    >
                      Eluve Admin Dashboard
                    </Heading>
                  </div>
                  <div className="flex flex-wrap gap-1 md:gap-2">
                    <LinkButton
                      icon={{ name: 'LogOut' }}
                      spacing="compact"
                      text="Return to App"
                      to="/"
                      type="subtle"
                    />
                  </div>
                </div>
                <Box className="mt-4">
                  <TabNav tabs={adminRoutes} colorMode="dark" />
                </Box>
              </div>
            </header>
          )}
        </div>

        <main className={shouldHideNavigation ? '-mt-24' : '-mt-32'}>
          <div className="mx-auto w-full px-2 pb-2 lg:px-8 lg:pb-8">
            <div
              className={`min-h-96 rounded-lg bg-white px-5 py-6 shadow sm:px-6 ${shouldHideNavigation ? 'pt-1' : ''}`}
            >
              <div className="w-full">
                {!shouldHideNavigation && <AdminBreadcrumbs />}
                <PrivacyToggleProvider>
                  <Suspense fallback={<LoadingIndicator />}>
                    <Outlet />
                  </Suspense>
                </PrivacyToggleProvider>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};
